import { useMemo, useState } from 'react';
import {
  CatalogFilterLayout,
  EntitySearchBar,
  EntityKindPicker,
  EntityTagPicker,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { featureFlagsApiRef, useApi } from '@backstage/frontend-plugin-api';
import { TemplateGroups } from '@backstage/plugin-scaffolder-react/alpha';
import type { TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import type { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { CategoryFilter } from './CategoryFilter';
import { StarredFilter } from './StarredFilter';
import { getCategories, getCategoryLabel } from './templateCategories';

// Renders the filters and template groups. Lives inside `EntityListProvider`
// so it can call `useEntityList` to read the unfiltered backend entities used
// to build the category list.
export const FilteredTemplates = ({
  templateFilter,
  TemplateCardComponent,
  onTemplateSelected,
}: {
  templateFilter: TemplateListPageProps['templateFilter'];
  TemplateCardComponent: TemplateListPageProps['TemplateCardComponent'];
  onTemplateSelected: (template: TemplateEntityV1beta3) => void;
}) => {
  const { backendEntities } = useEntityList();
  const { isStarredEntity } = useStarredEntities();
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const showExperimentalTemplates = featureFlagsApi.isActive('experimental-templates');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [starredOnly, setStarredOnly] = useState(false);

  const allCategories = useMemo(() => getCategories(backendEntities), [backendEntities]);

  const composedTemplateFilter = useMemo(() => {
    return (entity: TemplateEntityV1beta3) => {
      if (!showExperimentalTemplates && entity.metadata.tags?.includes('experimental')) {
        return false;
      }
      if (starredOnly && !isStarredEntity(entity)) {
        return false;
      }
      return templateFilter ? templateFilter(entity) : true;
    };
  }, [starredOnly, isStarredEntity, templateFilter, showExperimentalTemplates]);

  const groups = useMemo(() => {
    const categoriesToShow = selectedCategories.length === 0 ? allCategories : selectedCategories;
    return [...categoriesToShow]
      .sort((a, b) => getCategoryLabel(a).localeCompare(getCategoryLabel(b)))
      .map(category => ({
        title: getCategoryLabel(category),
        filter: (entity: TemplateEntityV1beta3) => entity.spec.type.toLowerCase() === category,
      }));
  }, [allCategories, selectedCategories]);

  const handleToggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(_starred => _starred !== category)
        : [...prev, category],
    );
  };

  const handleToggleStarred = () => {
    setStarredOnly(prev => !prev);
  };

  return (
    <CatalogFilterLayout>
      <CatalogFilterLayout.Filters>
        <EntitySearchBar />
        <EntityKindPicker initialFilter="template" hidden />
        <CategoryFilter
          allCategories={allCategories}
          selectedCategories={selectedCategories}
          onToggle={handleToggleCategory}
        />
        <StarredFilter starredOnly={starredOnly} onToggle={handleToggleStarred} />
        <EntityTagPicker />
      </CatalogFilterLayout.Filters>
      <CatalogFilterLayout.Content>
        <TemplateGroups
          groups={groups}
          templateFilter={composedTemplateFilter}
          TemplateCardComponent={TemplateCardComponent}
          onTemplateSelected={onTemplateSelected}
        />
      </CatalogFilterLayout.Content>
    </CatalogFilterLayout>
  );
};
